/** The confirmation of an itinerary's one-step action (plan O1, D6) — Publish now, Unpublish
 *  now, Regenerate deep link now: the popover on the Actions button that started it, never a modal
 *  (the records' "… now" anatomy, `RecordActionsProvider`). It happens on the server at once,
 *  not in the draft.
 *
 *  Confirm → running → the receipt, or why nothing happened:
 *
 *  - **Confirm** names what happens, one line each (`lines`: where the app shows the itinerary
 *    then, the app's refresh, a shared catalog itinerary, …), and that it is saved at once with
 *    an audit entry. The confirm button is focused: `Enter` confirms (its ↵ key cap is out of
 *    the button's name), `Esc` or **Cancel** backs out.
 *  - **Running**: it cannot close.
 *  - **The receipt** (`receipt`): the headline with the audit number, the server's
 *    consequences in its words, the audit row and change history links — the audit trail of
 *    the action, one click away.
 *  - **Refused** (`refusal`): "Nothing was changed: <why>" in the operator's terms, with **Try
 *    again** when another try may answer (someone else was saving). */
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Alert, Box, Button, LinearProgress, Link, Popover, Typography } from '@mui/material';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Kbd } from '@/features/records/page/Kbd';

const PAPER_SX = { p: 1.5, mt: 0.5, width: 360, maxWidth: 'calc(100vw - 32px)', display: 'grid', gap: 1 } as const;
const LIST_SX = { m: 0, pl: 2.25, display: 'grid', gap: 0.25, typography: 'body2' } as const;

/** Said under every confirmation: the action is not part of the draft. */
export const AT_ONCE_NOTE = 'Happens at once, with an audit entry — not part of the draft.';

export interface ItineraryNowReceipt {
  /** "Published 8 Oct 11:02 UTC · audit #99303". */
  headline: string;
  /** What it means, in the server's words. */
  lines: readonly string[];
  links: readonly { label: string; to: string }[];
}

export interface ItineraryNowRefusal {
  text: string;
  /** Another try may answer: Try again. */
  retry: boolean;
}

type Phase = 'confirm' | 'running' | 'done' | 'failed';

export interface ItineraryNowPopoverProps<R> {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** "Publish now". */
  title: string;
  /** The itinerary, by the name the app shows. */
  subject: string;
  /** What happens, one line each. */
  lines: readonly string[];
  /** The confirm button's words ("Publish now"). */
  confirmLabel: string;
  run: () => Promise<R>;
  receipt: (result: R) => ItineraryNowReceipt;
  refusal: (e: unknown) => ItineraryNowRefusal;
  /** `data-itinerary-now` on the paper. */
  dataKey: string;
}

/** Mount it per opening (`key`): a closed popover forgets its outcome. */
export function ItineraryNowPopover<R>({
  anchorEl,
  onClose,
  title,
  subject,
  lines,
  confirmLabel,
  run,
  receipt,
  refusal,
  dataKey,
}: ItineraryNowPopoverProps<R>) {
  const titleId = useId();
  const [phase, setPhase] = useState<Phase>('confirm');
  const [done, setDone] = useState<ItineraryNowReceipt | null>(null);
  const [failed, setFailed] = useState<ItineraryNowRefusal | null>(null);
  const running = phase === 'running';

  const confirm = () => {
    setPhase('running');
    run().then(
      (r) => {
        setDone(receipt(r));
        setPhase('done');
      },
      (e: unknown) => {
        setFailed(refusal(e));
        setPhase('failed');
      },
    );
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={() => {
        if (!running) onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: phase === 'done' ? 'dialog' : 'alertdialog',
          'aria-labelledby': titleId,
          'aria-busy': running || undefined,
          'data-itinerary-now': dataKey,
          'data-phase': phase,
          sx: PAPER_SX,
        } as object,
      }}
    >
      <Box>
        <Typography id={titleId} variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <BoltOutlinedIcon aria-hidden sx={{ fontSize: 18 }} />
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {subject}
        </Typography>
      </Box>

      {(phase === 'confirm' || running) && (
        <>
          <Box component="ul" sx={LIST_SX} data-now-lines>
            {lines.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {AT_ONCE_NOTE}
          </Typography>
          {running && <LinearProgress aria-label={`${title}: running`} sx={{ height: 2 }} />}
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} disabled={running}>
              Cancel
            </Button>
            <Button size="small" variant="contained" autoFocus onClick={confirm} disabled={running} data-confirm-now>
              {confirmLabel}
              {/* The key cap is a hint for the eye: the button is named by its words alone. */}
              {!running && (
                <Box component="span" aria-hidden>
                  <Kbd>↵</Kbd>
                </Box>
              )}
            </Button>
          </Box>
        </>
      )}

      {phase === 'failed' && failed && (
        <>
          <Alert severity="warning" sx={{ py: 0, '& .MuiAlert-message': { typography: 'body2', py: 0.75 } }}>
            Nothing was changed: {failed.text}
          </Alert>
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} autoFocus={!failed.retry}>
              Close
            </Button>
            {failed.retry && (
              <Button size="small" variant="outlined" onClick={confirm} autoFocus>
                Try again
              </Button>
            )}
          </Box>
        </>
      )}

      {phase === 'done' && done && (
        <Box data-receipt sx={{ display: 'grid', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
            <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 20, color: 'success.main', mt: '1px' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }} role="status">
              {done.headline}
            </Typography>
          </Box>
          {done.lines.length > 0 && (
            <Box component="ul" sx={LIST_SX} data-receipt-lines>
              {done.lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </Box>
          )}
          {done.links.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5 }}>
              {done.links.map((l) => (
                <Link key={l.label} component={RouterLink} to={l.to} variant="body2">
                  {l.label}
                </Link>
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" variant="outlined" onClick={onClose} autoFocus>
              Close
            </Button>
          </Box>
        </Box>
      )}
    </Popover>
  );
}
